'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface ToggleCardProps {
  title: string;
  description: string;
  icon?: string;
  badge?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export default function ToggleCard({
  title,
  description,
  icon = 'SparklesIcon',
  badge,
  checked,
  onChange,
}: ToggleCardProps) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-3 text-left rounded-xl p-3.5 border transition-colors duration-200 ${
        checked ? 'border-purple-500/50 bg-purple-500/[0.06]' : 'border-white/[0.08] bg-white/[0.02] hover:border-white/20'
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          checked ? 'bg-purple-500/20 text-purple-200' : 'bg-white/[0.05] text-gray-400'
        }`}
      >
        <Icon name={icon} size={18} variant="outline" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
          {title}
          {badge && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-purple-500/25 to-fuchsia-500/20 border border-purple-400/30 text-[9px] font-bold uppercase tracking-wide text-purple-200">
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 2l1.6 4.9L17.5 8l-4.9 1.6L11 14.5 9.4 9.6 4.5 8l4.9-1.1L11 2zm7 9l.9 2.7 2.7.9-2.7.9L18 18l-.9-2.6-2.7-.9 2.7-.9L18 11z" />
              </svg>
              AI
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500 leading-snug">{description}</p>
      </div>
      <span
        className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${
          checked ? 'bg-purple-600' : 'bg-white/[0.12]'
        }`}
      >
        <motion.span
          animate={{ left: checked ? 22 : 2 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow"
        />
      </span>
    </button>
  );
}
